import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { getProductImages } from "../../apis/Product/ProductImagesAPi";
import { baseUrl } from "../../apis/util";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  AlertCircle,
  ShieldCheck,
  Search,
  Plus,
  ChevronDown,
  LayoutGrid,
  Wrench,
  FolderKanban,
  Check,
  X,
} from "lucide-react";
import { Input } from "../BusinessDash/components/ui/input";
import { Button } from "../BusinessDash/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../BusinessDash/components/ui/select";
import { cn } from "../BusinessDash/lib/utils";
import ProductModal from "../NewProduct/components/ProductModal";

// ─── Sector options (exported for reuse) ───────────────────────────────────

export const SECTOR_OPTIONS = [
  { value: "construction", label: "Construction / Built environment" },
  { value: "textiles", label: "Textiles", coming: true },
  { value: "electronics", label: "Electronics", coming: true },
  { value: "furniture", label: "Furniture", coming: true },
  { value: "packaging", label: "Packaging products", coming: true },
  { value: "consumer", label: "Consumer products" },
  { value: "other", label: "Generic (Cross-sector)" },
];

const statusConfig = {
  completed: {
    label: "Completed",
    icon: CheckCircle2,
    color: "bg-emerald-100 text-emerald-700 border-emerald-200",
  },
  verified: {
    label: "Verified – Locked",
    icon: ShieldCheck,
    color: "bg-violet-100 text-violet-700 border-violet-200",
  },
  "in-progress": {
    label: "In progress",
    icon: Clock,
    color: "bg-amber-100 text-amber-700 border-amber-200",
  },
  draft: {
    label: "Draft",
    icon: AlertCircle,
    color: "bg-muted text-muted-foreground border-border",
  },
};

const objectTypeOptions = [
  { value: "product", label: "Product", icon: LayoutGrid },
  { value: "service", label: "Service", icon: Wrench },
  { value: "project", label: "Project", icon: FolderKanban },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mapLockToProductStatus(lcaLockStatus) {
  if (lcaLockStatus === "verified_locked") return "verified";
  if (lcaLockStatus === "locked") return "in-progress";
  return "draft";
}

function buildVariantDescription(product) {
  if (product.functional_unit_desc) return product.functional_unit_desc;
  const parts = [];
  (product.variation_categories || []).forEach((vc) => {
    (vc.values || []).forEach((v) => {
      if (v?.value) parts.push(v.value);
    });
  });
  return parts.length ? parts.join(" · ") : "—";
}

function firstVariationValue(product) {
  const vc = product.variation_categories?.[0];
  const v = vc?.values?.[0];
  return v?.value || "";
}

function standardLabel(product) {
  const t = (product.assessment_type || "PCF").toUpperCase();
  if (t === "LCA") return "EN 15804+A2";
  return "PCF (GHG Protocol)";
}

function lastUpdatedLabel(product) {
  if (product.updated_at) return String(product.updated_at).slice(0, 10);
  if (product.last_updated) return String(product.last_updated).slice(0, 10);
  return "—";
}

/**
 * Merge duplicate variation dimensions (same name, case-insensitive) and
 * de-duplicate value chips per dimension (case-insensitive).
 */
function dedupeVariationCategories(variationCategories) {
  const list = Array.isArray(variationCategories) ? variationCategories : [];
  const byNameKey = new Map();

  for (const vc of list) {
    const rawName =
      String(vc?.name ?? vc?.label ?? "").trim() || "Variation";
    const nameKey = rawName.toLowerCase();
    if (!byNameKey.has(nameKey)) {
      byNameKey.set(nameKey, { displayName: rawName, valueMap: new Map() });
    }
    const entry = byNameKey.get(nameKey);
    for (const val of vc?.values || []) {
      const s = val?.value != null ? String(val.value).trim() : "";
      if (!s) continue;
      const vKey = s.toLowerCase();
      if (!entry.valueMap.has(vKey)) {
        entry.valueMap.set(vKey, { id: val.id, value: s });
      }
    }
  }

  return Array.from(byNameKey.values()).map(({ displayName, valueMap }) => ({
    name: displayName,
    values: Array.from(valueMap.values()).sort((a, b) =>
      a.value.localeCompare(b.value, undefined, { sensitivity: "base" })
    ),
  }));
}

/** Turn relative API paths into absolute URLs when needed */
function normalizeMediaUrl(url) {
  if (url == null) return undefined;
  const s = String(url).trim();
  if (!s) return undefined;
  if (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("data:") ||
    s.startsWith("//")
  ) {
    return s.startsWith("//") ? `https:${s}` : s;
  }
  if (s.startsWith("/")) return s;
  const root = String(baseUrl || "").replace(/\/$/, "");
  return `${root}/${s.replace(/^\//, "")}`;
}

/**
 * First image URL from list/detail payload (PCF & LCA use same product-images API).
 * Backend often sends `images: [{ image, id, public_id }]`, not top-level `image`.
 */
function resolveProductImageUrl(product) {
  if (!product || typeof product !== "object") return undefined;

  const direct =
    product.image ||
    product.image_url ||
    product.thumbnail ||
    product.thumbnail_url ||
    product.cover_image ||
    product.coverImage ||
    product.product_image ||
    product.product_image_url ||
    product.main_image ||
    product.main_image_url ||
    product.photo ||
    product.photo_url;

  if (direct && String(direct).trim()) {
    return normalizeMediaUrl(direct);
  }

  const arrays = [product.images, product.product_images];
  for (const arr of arrays) {
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const first = arr[0];
    if (typeof first === "string" && String(first).trim()) {
      return normalizeMediaUrl(first);
    }
    if (first && typeof first === "object") {
      const u =
        first.image ||
        first.url ||
        first.image_url ||
        first.secure_url ||
        first.src;
      if (u && String(u).trim()) return normalizeMediaUrl(u);
    }
  }

  return undefined;
}

function mapApiProductToCard(product) {
  const categoryId =
    product.subgroup != null ? String(product.subgroup) : "";
  return {
    id: String(product.id),
    raw: product,
    name: product.name || "—",
    productNumber: product.product_num || "",
    variant: buildVariantDescription(product),
    variation: firstVariationValue(product),
    image: resolveProductImageUrl(product),
    status: mapLockToProductStatus(product.lca_lock_status),
    gwp: product.gwp_display || product.gwp || "—",
    standard: standardLabel(product),
    lastUpdated: lastUpdatedLabel(product),
    objectType: "product",
    category: categoryId,
  };
}

// ─── New Category Dialog ─────────────────────────────────────────────────────

function NewCategoryDialog({ onConfirm, onClose }) {
  const [label, setLabel] = useState("");
  /** Variation **dimension** names (e.g. Color, Size) — not option values like black/M */
  const [dimensionNames, setDimensionNames] = useState([]);
  const [dimensionInput, setDimensionInput] = useState("");

  const addDimension = () => {
    const v = dimensionInput.trim();
    if (
      v &&
      !dimensionNames.some((d) => d.toLowerCase() === v.toLowerCase())
    ) {
      setDimensionNames((prev) => [...prev, v]);
      setDimensionInput("");
    }
  };

  const removeDimension = (v) =>
    setDimensionNames((prev) => prev.filter((x) => x !== v));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-card border border-border rounded-2xl shadow-xl p-6 w-[420px] space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">Ny kategori</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Kategorinavn *
          </label>
          <Input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && label.trim() && addDimension()
            }
            placeholder="f.eks. Tagvinduer"
            className="h-9 text-sm"
          />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">
            Variationsdimensioner{" "}
            <span className="font-normal opacity-60">
              (valgfrit – fx Farve, Størrelse, Type)
            </span>
          </label>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Hvert navn bliver sin egen kategori. Værdier (fx sort, hvid) tilføjes på
            produktet.
          </p>
          <div className="flex gap-2">
            <Input
              value={dimensionInput}
              onChange={(e) => setDimensionInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addDimension()}
              placeholder="fx Farve, Mønster…"
              className="h-8 text-xs flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addDimension}
              className="h-8 text-xs px-3 shrink-0"
            >
              <Plus className="h-3 w-3 mr-1" /> Tilføj
            </Button>
          </div>
          {dimensionNames.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {dimensionNames.map((v) => (
                <span
                  key={v}
                  className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border border-border bg-muted text-foreground"
                >
                  {v}
                  <button
                    type="button"
                    onClick={() => removeDimension(v)}
                    className="text-muted-foreground hover:text-destructive ml-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Annuller
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!label.trim()}
            onClick={() => onConfirm(label.trim(), dimensionNames)}
          >
            Opret kategori
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Object type tiles (Lovable) — exported for page chrome (e.g. ProductV2) ──

/**
 * Lovable-style Product / Service / Project tiles.
 * `variant="compact"` — smaller tiles in the catalog header row (matches Lovable scale).
 */
export function CatalogObjectTypeTiles({
  selected,
  onChange,
  options,
  variant = "compact",
}) {
  const opts = options ?? objectTypeOptions;
  const isCompact = variant === "compact";
  return (
    <div className={cn("flex flex-wrap", isCompact ? "gap-2" : "gap-3")}>
      {opts.map((opt) => {
        const Icon = opt.icon;
        const isActive = selected === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "flex flex-col items-center justify-center border-2 transition-all duration-150 font-semibold bg-primary text-primary-foreground",
              isCompact
                ? "gap-1.5 h-[5.25rem] w-[5.25rem] rounded-xl text-[11px] leading-tight sm:h-[5.5rem] sm:w-[6rem]"
                : "gap-3 h-36 w-40 rounded-2xl text-sm",
              isActive
                ? "border-primary shadow-sm"
                : "border-primary/40 opacity-90 hover:opacity-100"
            )}
          >
            <Icon
              className={cn(isCompact ? "h-7 w-7" : "h-12 w-12")}
              strokeWidth={1.3}
            />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function PortfolioBox() {
  return (
    <div
      className="rounded-2xl border border-primary/20 px-8 py-7 flex flex-col justify-center h-full min-h-[200px]"
      style={{
        backgroundColor: "var(--catalog-navy-bg, rgba(33, 55, 97, 0.85))",
        color: "hsl(var(--primary-foreground))",
      }}
    >
      <h2 className="text-lg font-bold mb-4">Product Portfolio</h2>
      <p className="text-sm opacity-80 leading-relaxed mb-3">
        Here you can create, group, and manage the products your company produces.
      </p>
      <ul className="text-sm opacity-80 leading-relaxed space-y-1.5 list-disc list-inside mb-3">
        <li>Organise products into categories and variations</li>
        <li>Define functional units and lifecycle scope</li>
        <li>Track LCA calculation and verification status</li>
        <li>Manage supplier data and OpenCirc integration</li>
      </ul>
      <p className="text-sm opacity-80 leading-relaxed">
        This overview supports traceability, portfolio transparency, and regulatory
        readiness.
      </p>
    </div>
  );
}

function OpenCircBox({ stats }) {
  const rows = [
    { label: "Connected products", value: stats.connected },
    { label: "Products with updates", value: stats.updates },
    { label: "Pending activations", value: stats.pending },
    { label: "Last sync", value: stats.lastSync },
  ];
  return (
    <div
      className="rounded-2xl border border-border px-7 py-7 flex flex-col justify-between h-full min-h-[200px]"
      style={{
        backgroundColor: "var(--catalog-navy-bg, rgba(33, 55, 97, 0.85))",
        color: "hsl(var(--primary-foreground))",
      }}
    >
      <div>
        <h2 className="text-lg font-bold mb-1">OpenCirc Integration</h2>
        <p className="text-xs opacity-70 mb-5">
          Portfolio-level supplier data synchronization
        </p>
        <div className="space-y-2.5">
          {rows.map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between text-sm"
            >
              <span className="opacity-80">{item.label}</span>
              <span className="font-semibold">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-5 space-y-2">
        <div className="flex items-center gap-2 justify-end flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            type="button"
            className="h-8 text-xs rounded-full px-4 font-semibold"
          >
            View updates
          </Button>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            className="h-8 text-xs rounded-full px-4 font-semibold"
          >
            Sync all
          </Button>
        </div>
        <div className="text-right">
          <button
            type="button"
            className="text-[11px] underline opacity-60 hover:opacity-100 transition-opacity"
          >
            View sync log
          </button>
        </div>
      </div>
    </div>
  );
}

/** Lists subgroup variation dimensions (category names + value chips) — not a product grid toggle */
function CategoryVariationsPanel({ variationCategories }) {
  const list = dedupeVariationCategories(variationCategories);
  if (list.length === 0) {
    return (
      <div className="mb-3 w-max max-w-[min(100%,36rem)] rounded-xl border border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
        No variation dimensions are set up for this category yet. Add them when
        creating or editing products in this subgroup.
      </div>
    );
  }
  return (
    <div className="mb-3 w-max max-w-[min(100%,48rem)] rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
        Variation dimensions & types
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((vc) => (
          <div key={vc.name}>
            <p className="text-xs font-semibold text-foreground">
              {vc.name || vc.label || "Variation"}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {(vc.values || []).length === 0 ? (
                <span className="text-[10px] text-muted-foreground italic">
                  No values defined
                </span>
              ) : (
                (vc.values || []).map((val, idx) => (
                  <span
                    key={val.id ?? `${vc.name}-${val.value}-${idx}`}
                    className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-foreground"
                  >
                    {val.value}
                  </span>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductCard({ product, onClick }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [product.id, product.image]);

  const status = statusConfig[product.status];
  const StatusIcon = status.icon;
  const isPCF =
    product.standard.toLowerCase().includes("pcf") ||
    product.standard.toLowerCase().includes("ghg");
  const methodLabel = isPCF ? "PCF" : "LCA";
  const methodColor = isPCF
    ? "bg-amber-600/90 text-white"
    : "bg-emerald-700/90 text-white";

  const showImage = Boolean(product.image) && !imgFailed;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all duration-200 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-ring w-full"
    >
      <div className="relative h-36 w-full bg-muted overflow-hidden">
        {showImage ? (
          <img
            src={product.image}
            alt={product.name || "Product"}
            className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-4xl text-muted-foreground/30 select-none">
              ◻
            </span>
          </div>
        )}
        <span
          className={cn(
            "absolute top-2 left-2 text-[9px] font-bold px-2 py-0.5 rounded-full shadow-sm",
            methodColor
          )}
        >
          {methodLabel}
        </span>
        <span
          className={cn(
            "absolute top-2 right-2 flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border",
            status.color
          )}
        >
          <StatusIcon className="h-3 w-3" />
          {status.label}
        </span>
      </div>
      <div className="p-3 space-y-1.5">
        <p className="text-xs font-semibold text-foreground leading-tight group-hover:text-primary transition-colors line-clamp-2">
          {product.name}
        </p>
        <p className="text-[10px] text-muted-foreground line-clamp-2">
          {product.variant}
        </p>
        <div className="flex items-center justify-between pt-1 border-t border-border">
          <p className="text-[10px] text-muted-foreground">
            {product.lastUpdated}
          </p>
          <ArrowRight className="h-3 w-3 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
        </div>
      </div>
    </button>
  );
}

// ─── Main catalog ─────────────────────────────────────────────────────────────

/**
 * Lovable-style product catalog. Data comes from props (backend); no mock product rows.
 */
const ProductCatalog = ({
  subgroups = [],
  products = [],
  year = "",
  assessmentType = "",
  businessFallbackId = "",
  userAccess = "read",
  showObjectTypeTiles = false,
  /** Product / Service / Project switcher in column 1 above search (Lovable layout) */
  pageTypeTiles = null,
  onAddCategory,
  onSelectProduct,
  /** When set, product cards open in parent tab UI instead of navigating away */
  onOpenProductInTab,
  trackRecentSubgroup,
  openCircStats,
  recentDisplayName,
}) => {
  const navigate = useNavigate();
  const [activeObjectType, setActiveObjectType] = useState("product");
  const [searchField, setSearchField] = useState("name");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  /** category id → variations detail panel open (does not hide product cards) */
  const [variationsPanelOpen, setVariationsPanelOpen] = useState({});
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [modalSubgroupId, setModalSubgroupId] = useState(null);
  const [modalBusiness, setModalBusiness] = useState(null);
  /** product id → first image URL from get-product-image (list payload often omits images) */
  const [fetchedProductImages, setFetchedProductImages] = useState({});
  const imageFetchInFlightRef = useRef(new Set());

  const oc = openCircStats || {
    connected: "—",
    updates: "—",
    pending: "—",
    lastSync: "—",
  };

  const categories = useMemo(() => {
    return (subgroups || []).map((sg) => ({
      id: String(sg.id),
      label: sg.name || "—",
      variation_categories: Array.isArray(sg.variation_categories)
        ? sg.variation_categories
        : [],
    }));
  }, [subgroups]);

  useEffect(() => {
    imageFetchInFlightRef.current = new Set();
    setFetchedProductImages({});
  }, [year, assessmentType]);

  useEffect(() => {
    const list = (products || []).filter(
      (p) => p?.is_product !== false && p?.id != null
    );
    const ids = [
      ...new Set(
        list
          .filter((p) => !resolveProductImageUrl(p))
          .map((p) => String(p.id))
          .filter((id) => !imageFetchInFlightRef.current.has(id))
      ),
    ];

    ids.forEach((id) => imageFetchInFlightRef.current.add(id));

    let cancelled = false;
    const BATCH = 6;

    const run = async () => {
      for (let i = 0; i < ids.length && !cancelled; i += BATCH) {
        const chunk = ids.slice(i, i + BATCH);
        await Promise.all(
          chunk.map(async (id) => {
            try {
              const res = await getProductImages({ productId: id });
              const url = res?.data?.images?.[0]?.image;
              const normalized = url ? normalizeMediaUrl(url) : null;
              if (!cancelled) {
                setFetchedProductImages((prev) => ({
                  ...prev,
                  [id]: normalized || null,
                }));
              }
            } catch {
              if (!cancelled) {
                setFetchedProductImages((prev) => ({ ...prev, [id]: null }));
              }
            } finally {
              imageFetchInFlightRef.current.delete(id);
            }
          })
        );
      }
    };

    if (ids.length > 0) run();

    return () => {
      cancelled = true;
      ids.forEach((id) => imageFetchInFlightRef.current.delete(id));
    };
  }, [products, year, assessmentType]);

  const cardProducts = useMemo(() => {
    const list = (products || []).filter((p) => {
      if (p.is_product === false) return false;
      if (!showObjectTypeTiles) return true;
      return (p._objectType || "product") === activeObjectType;
    });
    return list.map((p) => {
      const card = mapApiProductToCard(p);
      const id = String(p.id);
      const fetched = fetchedProductImages[id];
      if (!card.image && typeof fetched === "string" && fetched) {
        return { ...card, image: fetched };
      }
      return card;
    });
  }, [products, showObjectTypeTiles, activeObjectType, fetchedProductImages]);

  const visibleProducts = useMemo(() => {
    let list = cardProducts;
    const q = appliedQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      if (searchField === "name") {
        return (
          p.name.toLowerCase().includes(q) ||
          p.variant.toLowerCase().includes(q) ||
          (p.productNumber && p.productNumber.toLowerCase().includes(q))
        );
      }
      if (searchField === "standard") {
        return p.standard.toLowerCase().includes(q);
      }
      if (searchField === "category") {
        const cat = categories.find((c) => c.id === p.category);
        return cat?.label?.toLowerCase().includes(q);
      }
      return true;
    });
  }, [cardProducts, appliedQuery, searchField, categories]);

  const handleSearch = () => setAppliedQuery(searchQuery);

  const openAddProduct = useCallback(
    (categoryId) => {
      const sg = subgroups.find((s) => String(s.id) === String(categoryId));
      setModalSubgroupId(categoryId);
      setModalBusiness(
        sg?.business != null ? sg.business : businessFallbackId
      );
      setProductModalOpen(true);
    },
    [subgroups, businessFallbackId]
  );

  const navigateToProduct = useCallback(
    (product) => {
      const raw = product.raw;
      const business = raw?.business ?? businessFallbackId;
      const y = year || "";
      const at =
        assessmentType || raw?.assessment_type
          ? `&assessment_type=${encodeURIComponent(
              assessmentType || raw?.assessment_type || ""
            )}`
          : "";
      const yq = y ? `&year=${encodeURIComponent(y)}` : "";
      const isPCF =
        product.standard.toLowerCase().includes("pcf") ||
        product.standard.toLowerCase().includes("ghg");
      if (onSelectProduct) {
        onSelectProduct(raw.id, product.name, isPCF ? "pcf" : "lca");
        return;
      }
      if (onOpenProductInTab) {
        onOpenProductInTab({
          id: String(raw.id),
          name: product.name,
          method: isPCF ? "pcf" : "lca",
          business: business != null ? String(business) : "",
          assessmentType:
            assessmentType || raw?.assessment_type || (isPCF ? "PCF" : "LCA"),
        });
        return;
      }
      navigate(
        `/product/${raw.id}?business=${encodeURIComponent(business)}${yq}${at}`
      );
    },
    [
      navigate,
      year,
      assessmentType,
      businessFallbackId,
      onSelectProduct,
      onOpenProductInTab,
    ]
  );

  const handleCardClick = useCallback(
    (product) => {
      const sg = subgroups.find(
        (s) => String(s.id) === String(product.raw?.subgroup)
      );
      if (sg && trackRecentSubgroup) trackRecentSubgroup(sg);
      navigateToProduct(product);
    },
    [subgroups, trackRecentSubgroup, navigateToProduct]
  );

  const useLovableHeaderRow = Boolean(pageTypeTiles) || showObjectTypeTiles;

  return (
    <div className="space-y-6">
      <div
        className={cn(
          "grid grid-cols-1 gap-6 items-start",
          useLovableHeaderRow
            ? "lg:grid-cols-[auto_1fr_min(26rem,1fr)] xl:grid-cols-[auto_1fr_420px]"
            : "lg:grid-cols-[minmax(220px,1fr)_1fr_420px]"
        )}
      >
        <div className="flex min-w-0 flex-col gap-3">
          {pageTypeTiles ? (
            <CatalogObjectTypeTiles
              selected={pageTypeTiles.selected}
              onChange={pageTypeTiles.onChange}
              options={pageTypeTiles.options}
              variant="compact"
            />
          ) : null}

          {showObjectTypeTiles && (
            <CatalogObjectTypeTiles
              selected={activeObjectType}
              onChange={setActiveObjectType}
              variant="compact"
            />
          )}

          {(recentDisplayName || visibleProducts[0]) && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-muted-foreground">
                Recent:
              </span>
              <span className="px-3 py-1 rounded-full bg-muted border border-border text-xs text-muted-foreground max-w-[240px] truncate">
                {recentDisplayName
                  ? recentDisplayName.split("–")[0].trim()
                  : visibleProducts[0].name.split("–")[0].trim()}
              </span>
            </div>
          )}

          <div className="flex flex-col gap-2 mt-1">
            <div className="flex items-center gap-0 rounded-full border border-border overflow-hidden bg-background shadow-sm max-w-xl">
              <div className="border-r border-border shrink-0">
                <Select value={searchField} onValueChange={setSearchField}>
                  <SelectTrigger className="h-10 border-0 rounded-none rounded-l-full bg-transparent pl-4 pr-2 text-sm font-medium focus:ring-0 shadow-none w-[7.5rem]">
                    <SelectValue />
                  </SelectTrigger>
                  {/* Explicit slate/white: Radix portals to body, outside .lov-catalog-root */}
                  <SelectContent className="z-[200] rounded-md border border-slate-200 bg-white text-slate-900 shadow-lg">
                    <SelectItem
                      value="name"
                      className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
                    >
                      Name
                    </SelectItem>
                    <SelectItem
                      value="standard"
                      className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
                    >
                      Standard
                    </SelectItem>
                    <SelectItem
                      value="category"
                      className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
                    >
                      Category
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="relative flex-1 min-w-0">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search by name..."
                  className="border-0 rounded-none rounded-r-full h-10 pl-4 pr-10 text-sm focus-visible:ring-0 shadow-none bg-transparent"
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleSearch}
              className="self-start h-8 px-4 text-xs rounded-full font-semibold"
            >
              Search results
            </Button>
          </div>
        </div>

        <PortfolioBox />
        <OpenCircBox stats={oc} />
      </div>

      {userAccess === "write" && (
        <div className="flex items-center gap-2 pt-4 flex-wrap">
          <span className="px-4 py-1.5 rounded-full border border-border bg-muted text-sm font-medium text-foreground">
            Category
          </span>
          <button
            type="button"
            onClick={() => setShowNewCategory(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-border bg-muted text-sm font-medium text-foreground hover:bg-muted/80 transition-colors"
          >
            Add category <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="space-y-5">
        {categories.length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-xl">
            No categories yet.{" "}
            {userAccess === "write"
              ? 'Use "Add category" to create one.'
              : ""}
          </p>
        )}
        {categories.map((category) => {
          const categoryProducts = visibleProducts.filter(
            (p) => p.category === category.id
          );
          if (appliedQuery.trim() && categoryProducts.length === 0) return null;

          const variationsOpen = Boolean(variationsPanelOpen[category.id]);

          return (
            <div key={category.id}>
              <div
                className="inline-flex max-w-full flex-wrap items-center gap-2 mb-3 px-4 py-2 rounded-full text-sm font-semibold"
                style={{
                  backgroundColor:
                    "var(--catalog-navy-bg, rgba(33, 55, 97, 0.85))",
                  color: "hsl(var(--primary-foreground))",
                }}
              >
                <Check className="h-3.5 w-3.5 opacity-80 shrink-0" />
                <span className="shrink-0">{category.label}</span>
                <button
                  type="button"
                  onClick={() =>
                    setVariationsPanelOpen((prev) => ({
                      ...prev,
                      [category.id]: !prev[category.id],
                    }))
                  }
                  className={cn(
                    "flex items-center gap-1 ml-1 text-xs font-normal opacity-90 hover:opacity-100 rounded-full pl-2 pr-1 py-0.5 -mr-1",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/40 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                  )}
                  aria-expanded={variationsOpen}
                  aria-controls={`variations-panel-${category.id}`}
                >
                  Variations
                  <ChevronDown
                    className={cn(
                      "h-3 w-3 transition-transform shrink-0",
                      variationsOpen ? "rotate-0" : "-rotate-90"
                    )}
                  />
                </button>
              </div>

              {variationsOpen && (
                <div
                  id={`variations-panel-${category.id}`}
                  className="w-max max-w-[min(100%,48rem)]"
                >
                  <CategoryVariationsPanel
                    variationCategories={category.variation_categories}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {categoryProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onClick={() => handleCardClick(product)}
                  />
                ))}

                {userAccess === "write" && (
                  <button
                    type="button"
                    onClick={() => openAddProduct(category.id)}
                    className="rounded-xl border border-dashed border-border hover:border-primary/40 bg-muted/30 min-h-[180px] flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary transition-colors group"
                  >
                    <span className="text-2xl font-light group-hover:scale-110 transition-transform">
                      +
                    </span>
                    <span className="text-xs">Add Product</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showNewCategory && (
        <NewCategoryDialog
          onConfirm={(categoryLabel, dimensionNames) => {
            if (onAddCategory) onAddCategory(categoryLabel, dimensionNames);
            setShowNewCategory(false);
          }}
          onClose={() => setShowNewCategory(false)}
        />
      )}

      {productModalOpen && modalSubgroupId != null && (
        <ProductModal
          onClose={() => {
            setProductModalOpen(false);
            setModalSubgroupId(null);
          }}
          year={year}
          subgroupId={modalSubgroupId}
          business={modalBusiness}
          subgroups={subgroups}
          assessmentType={assessmentType || "PCF"}
        />
      )}
    </div>
  );
};

export default ProductCatalog;
